import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button } from 'react-bootstrap'; 
import { useParams, useNavigate } from 'react-router-dom';
import styles from './Report.module.scss';
import api from "../../services/api";
import Loading from '../Shared/Loading'
import { useTranslation } from 'react-i18next';

import TemplateSelector from './TemplateSelector';
import Blocks from './Blocks';
import { useToastContext } from "../../toasts/toast-context";
import ModalConfirm from '../Shared/ModalConfirm';

function Report(props, ref) {
  const {
    reportUrl,
    completeReportRedirectUrl,
    reportLoadCompleteCallback,
    savedTab,
    savedScroll    
  } = props;

  let { consultationUid } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const protocolRef = useRef(null);
  const [templateUid, setTemplateUid] = useState(null);
  const [templateList, setTemplateList] = useState([]);  
  const { showSuccessToastMessage, showErrorToastMessage } = useToastContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const reportSaved = useRef(false);
  const autoSaveIntervalRef = useRef(null);
  let navigate = useNavigate();

  const scrollContainerRef = useRef();
  const [activeBlockTab, setActiveBlockTab] = useState(savedTab);

  useImperativeHandle(ref, () => ({
    getScrollInfo: () => ({
      scrollTop: scrollContainerRef.current?.scrollTop ?? 0,
      tab: activeBlockTab
    })
  }));

  useEffect(() => {
    const loadReport = () => {
      api
        .get(reportUrl)
        .then((response) => {
          console.log(response.data);
          setReport(response.data.blocks);
          setIsReadOnly(response.data.isReadOnly);
          setTemplateList(response.data.templateList);
          setTemplateUid(response.data.selectedTemplateUid);
          setLoading(false);
          if (reportLoadCompleteCallback) {
            reportLoadCompleteCallback({
              consultationRecordUid: response.data.consultationRecordUid,
              isReadOnly: response.data.isReadOnly
            });
          }
        }, (error) => {
          setLoading(false);
        });
    }    

    loadReport();
   
  }, [consultationUid]);

  useEffect(() => {
    if (report === null)
      return;

    protocolRef.current = report;

    // Start periodic saving every 30 seconds
    autoSaveIntervalRef.current = setInterval(() => {
      // Only save if not readonly
      if (!isReadOnly) {
        saveReport();
      }
    }, 30000);

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current); // Clear interval on component unmount
      }
      if (!reportSaved.current && !isReadOnly)
        saveReport();      
    };
  }, [report, isReadOnly]);

  useEffect(() => {
    if (!loading && scrollContainerRef.current && savedScroll !== undefined) {
      const el = scrollContainerRef.current;

      const tryRestoreScroll = () => {
        if (el.scrollHeight > el.clientHeight) {
          el.scrollTop = savedScroll;
        } else {
          requestAnimationFrame(tryRestoreScroll);
        }
      };

      requestAnimationFrame(tryRestoreScroll);
    }
  }, [loading]);


  const onSaveSendButtonClick = () => {
    setShowConfirmModal(true);
  };

  const onConfirmSaveAndSend = () => {
    setShowConfirmModal(false);
    // Clear auto-save interval immediately to prevent any further save attempts
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }
    // Set readonly immediately to prevent any race conditions
    setIsReadOnly(true);
    saveReport(true);
    reportSaved.current = true;
  };

  const onCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const saveReport = (markCompleted) => {    
    // Don't save if report is readonly (unless we're marking it as completed)
    if (isReadOnly && !markCompleted) {
      return;
    }
    
    let items = [];
    let report = protocolRef.current;

    if (report.length > 0) {
      report.forEach(block => {
        block.headers.forEach(header => {
          header.items.forEach(item => {
            if (!item.isReadOnly) {
              items.push({ uid: item.uid, value: { text: item.textValue, optionUid: item.options?.selectedOption } });
            }
          });       
        });
      });
    }    

    if (items.length > 0) {
      api
        .post(`${reportUrl}/save`, {
          items: items,
          isReportCompleted: markCompleted
        }).then((response) => {
          showSuccessToastMessage(t('consultation.saving.success'));
          if (markCompleted) {
            navigate(`${completeReportRedirectUrl}`);
          };
        }, (error) => {
          showErrorToastMessage(t('consultation.saving.error'));
        });
    }
  };

  const onTemplateChanged = (templateUid) => {    
    api
      .post(`${reportUrl}/change`, {
        templateUid: templateUid,
      })
      .then((response) => {
        console.log(response.data);
        setReport(response.data.blocks);        
      }, (error) => {        
      });
  };

  if (loading)
    return <Loading />;

  return (
    <>
      <ModalConfirm
        show={showConfirmModal}
        onHide={onCancelConfirm}
        header={t('consultation.expert.addReport.confirmComplete.header')}
        body={t('consultation.expert.addReport.confirmComplete.body')}
        buttonConfirmText={t('consultation.expert.addReport.confirmComplete.confirmButton')}
        buttonCancelText={t('consultation.expert.addReport.confirmComplete.cancelButton')}
        onConfirmClick={onConfirmSaveAndSend}
      />
      <div className={styles.fullScreenForm}>
        <div className={styles.innerScroll} ref={scrollContainerRef}>
          <div className={styles.inner}>
            {!isReadOnly &&
              <div className={styles.selector}>
                <TemplateSelector templateList={templateList} activeTemplateUid={templateUid} onTemplateChangedCallback={onTemplateChanged} />
                {!isReadOnly && <Button variant="primary" className={styles.stickyButton} onClick={onSaveSendButtonClick}>{t('consultation.expert.addReport.saveAndSend')}</Button>}
              </div>
            }
            <div className={styles.tabs}>
              <Blocks
                blocks={report}
                reportUrl={reportUrl}                
                savedTab={savedTab}
                isReadOnly={isReadOnly}
                onTabChange={(tabKey) => {
                  setActiveBlockTab(tabKey);
                  // Only save on tab change if not readonly
                  if (!isReadOnly) {
                    saveReport();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default forwardRef(Report);
